import { ACCESS_TOKEN, REFRESH_TOKEN } from "../constants";
import { API_BASE_URL, ENDPOINTS } from "../config";

export interface FetchOptions {
  method?: string;
  headers?: Record<string, string>;
  body?: any;
  credentials?: RequestCredentials;
}

export interface ApiResponse<T> {
  data?: T;
  error?: any;
  status: number;
}

export function getAccessToken(): string | null {
  return localStorage.getItem(ACCESS_TOKEN);
}

function getRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_TOKEN);
}

function storeTokens(accessToken: string, refreshToken?: string) {
  localStorage.setItem(ACCESS_TOKEN, accessToken);
  if (refreshToken) {
    localStorage.setItem(REFRESH_TOKEN, refreshToken);
  }
}

async function tryRefreshToken(): Promise<string | null> {
  const refresh = getRefreshToken();
  if (!refresh) return null;

  try {
    const r = await fetch(`${API_BASE_URL}${ENDPOINTS.refresh}`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ refresh }),
    });

    if (!r.ok) return null;

    const parsed = await r.json();
    const newAccess = parsed?.access ?? parsed?.access_token ?? null;
    if (newAccess) {
      storeTokens(newAccess, parsed?.refresh ?? parsed?.refresh_token);
      return newAccess;
    }
    return null;
  } catch {
    return null;
  }
}

export async function fetchClient<T>(
  endpoint: string,
  options: FetchOptions = {}
): Promise<ApiResponse<T>> {
  const normalizedEndpoint = endpoint.startsWith("/") ? endpoint : `/${endpoint}`;
  const url = `${API_BASE_URL}${normalizedEndpoint}`;

  let token = getAccessToken();

  const headers: Record<string, string> = {
    Accept: "application/json",
    ...(options.headers || {}),
  };

  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  if (options.body !== undefined && !(options.body instanceof FormData)) {
    headers["Content-Type"] = headers["Content-Type"] ?? "application/json";
  }

  const doFetch = (authHeaders: Record<string, string>) =>
    fetch(url, {
      method: options.method ?? "GET",
      headers: authHeaders,
      mode: "cors",
      cache: "no-cache",
      body:
        options.body instanceof FormData
          ? options.body
          : options.body
            ? JSON.stringify(options.body)
            : undefined,
      credentials: "include",
    });

  try {
    let resp = await doFetch(headers);

    let parsed: any = undefined;
    try {
      parsed = await resp.json();
    } catch {
      parsed = undefined;
    }

    if (resp.status === 401) {
      const newAccess = await tryRefreshToken();
      if (newAccess) {
        headers["Authorization"] = `Bearer ${newAccess}`;
        resp = await doFetch(headers);
        try {
          parsed = await resp.json();
        } catch {
          parsed = undefined;
        }
      }
    }

    if (!resp.ok) {
      return {
        error: parsed ?? { statusText: resp.statusText, status: resp.status },
        status: resp.status,
      };
    }

    return { data: parsed as T, status: resp.status };
  } catch (networkError) {
    console.error("[fetchClient] Network error:", networkError);
    return {
      error: { message: "Network error", detail: String(networkError) },
      status: 0,
    };
  }
}
